/**
 * LegalDocument — layout partagé pour les 3 pages légales (Phase 11).
 *
 * Rend :
 *  - un en-tête (eyebrow, titre, sous-titre, date "dernière mise à jour")
 *  - un sommaire ancré (si sections.length > 3)
 *  - le corps : sections numérotées, chacune avec ses blocs typés
 *  - une note bilingue (EN → "This is a courtesy translation...")
 *  - contact final
 *
 * Bloc types acceptés :
 *  - { type: 'p',  content: 'texte' }         → paragraphe
 *  - { type: 'ul', content: ['item 1', ...] } → liste à puces
 *  - { type: 'ol', content: ['item 1', ...] } → liste ordonnée
 *  - { type: 'h3', content: 'sous-titre' }    → sous-titre au sein d'une section
 *  - { type: 'note', content: 'texte' }       → encart mise en garde
 *
 * Ancre / sommaire : chaque section a un `id` unique utilisé comme id HTML (?section=id supporté).
 */
import { useEffect } from 'react';
import { Navbar } from '@/components/site/Navbar';
import { Footer } from '@/components/site/Footer';
import { useT } from '@/lib/i18n';
import { setPageMeta } from '@/lib/seo';
import { AlertTriangle, Info } from 'lucide-react';

function Block({ block }) {
  const type = block.type || 'p';
  const c = block.content;
  if (type === 'h3') {
    return (
      <h3 className="mt-6 text-[15px] font-bold text-[var(--om-ink)]">{c}</h3>
    );
  }
  if (type === 'ul' && Array.isArray(c)) {
    return (
      <ul className="mt-3 space-y-2 list-disc pl-5 text-[14.5px] leading-relaxed text-[var(--om-ink-soft)]">
        {c.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    );
  }
  if (type === 'ol' && Array.isArray(c)) {
    return (
      <ol className="mt-3 space-y-2 list-decimal pl-5 text-[14.5px] leading-relaxed text-[var(--om-ink-soft)]">
        {c.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ol>
    );
  }
  if (type === 'note') {
    return (
      <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-[13.5px] leading-relaxed text-amber-900 dark:text-amber-100">
        <AlertTriangle size={14} strokeWidth={2.4} className="mt-0.5 shrink-0" />
        <div>{c}</div>
      </div>
    );
  }
  return (
    <p className="mt-3 text-[14.5px] leading-relaxed text-[var(--om-ink-soft)] whitespace-pre-wrap">
      {c}
    </p>
  );
}

export function LegalDocument({ doc, path, testid = 'page-legal', courtesyNote }) {
  const t = useT();

  useEffect(() => {
    setPageMeta({
      title: doc.seoTitle || doc.title,
      description: doc.seoDescription || (doc.subtitle || doc.title).slice(0, 200),
      path,
    });
    // Scroll to anchor if present
    if (typeof window !== 'undefined' && window.location.hash) {
      const el = document.getElementById(window.location.hash.slice(1));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
      } else {
        window.scrollTo({ top: 0, behavior: 'instant' });
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [doc, path]);

  const showToc = (doc.sections || []).length > 3;

  return (
    <div data-testid={testid}>
      <Navbar />
      <main className="pt-[128px] md:pt-[168px] pb-24">
        <div className="om-container max-w-3xl">
          <span className="om-eyebrow" data-testid="legal-eyebrow">{doc.eyebrow || t('legal.eyebrow')}</span>
          <h1 className="om-h1 mt-4 text-[32px] sm:text-[40px] lg:text-[46px] text-[var(--om-ink)]" data-testid="legal-title">
            {doc.title}
          </h1>
          {doc.subtitle && (
            <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-[var(--om-muted)]" data-testid="legal-subtitle">
              {doc.subtitle}
            </p>
          )}
          <div className="mt-4 text-xs uppercase tracking-wider text-[var(--om-muted-2)]" data-testid="legal-updated">
            {t('legal.last_updated')} · {doc.updated}
          </div>

          {doc.draftNotice && (
            <div
              className="mt-6 flex items-start gap-2.5 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-[13.5px] leading-relaxed text-amber-900 dark:text-amber-100"
              data-testid="legal-draft-notice"
              role="note"
            >
              <AlertTriangle size={14} strokeWidth={2.4} className="mt-0.5 shrink-0" />
              <div>{doc.draftNotice}</div>
            </div>
          )}

          {courtesyNote && (
            <div
              className="mt-6 flex items-start gap-2.5 rounded-2xl border border-[var(--om-border)] bg-[var(--om-bg-pure)] px-4 py-3 text-[13px] italic leading-relaxed text-[var(--om-muted)]"
              data-testid="legal-courtesy"
            >
              <Info size={14} strokeWidth={2.4} className="mt-0.5 shrink-0" />
              <div>{courtesyNote}</div>
            </div>
          )}

          {showToc && (
            <nav
              className="mt-8 rounded-2xl border border-[var(--om-border)] bg-[var(--om-bg-pure)] p-5"
              aria-label={t('legal.toc_label')}
              data-testid="legal-toc"
            >
              <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">
                {t('legal.toc_label')}
              </div>
              <ol className="mt-3 grid grid-cols-1 gap-1.5 text-[13.5px] sm:grid-cols-2">
                {doc.sections.map((s, i) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="inline-flex items-baseline gap-2 text-[var(--om-ink-soft)] hover:text-[var(--om-ink)] hover:underline underline-offset-4"
                      data-testid={`legal-toc-link-${s.id}`}
                    >
                      <span className="tabular-nums text-[11px] font-bold text-[var(--om-muted-2)]">
                        {String(i + 1).padStart(2, '0')}.
                      </span>
                      <span>{s.title}</span>
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          )}

          <article className="mt-10 space-y-10">
            {doc.sections.map((s, i) => (
              <section key={s.id} id={s.id} data-testid={`legal-section-${s.id}`} className="scroll-mt-32">
                <h2 className="text-[20px] font-extrabold text-[var(--om-ink)]">
                  <span className="mr-2 tabular-nums text-[var(--om-muted-2)]">{String(i + 1).padStart(2, '0')}.</span>
                  {s.title}
                </h2>
                <div>
                  {(s.blocks || []).map((b, j) => (
                    <Block key={j} block={b} />
                  ))}
                </div>
              </section>
            ))}
          </article>

          {doc.footer && (
            <div className="mt-14 rounded-2xl border border-[var(--om-border)] bg-[var(--om-bg-pure)] p-5 text-[13.5px] leading-relaxed text-[var(--om-ink-soft)]" data-testid="legal-footer-block">
              <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">
                {doc.footer.title || t('legal.contact_title')}
              </div>
              <p className="mt-2 whitespace-pre-line">{doc.footer.body}</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
